import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Plus, RefreshCw, Save } from 'lucide-react';
import { BriefingSessionCard, DemoRoundCard } from './HRPerformanceModule';

const API = process.env.REACT_APP_BACKEND_URL;

const buildSession = (index, topics, existing) => ({
  ...(existing || {}),
  id: existing?.id || `briefing_${index + 1}`,
  session_number: existing?.session_number || index + 1,
  topics: existing?.topics?.length
    ? existing.topics
    : topics.map(t => ({ name: typeof t === 'string' ? t : t.name, covered: false })),
  feedback: existing?.feedback || '',
  status: existing?.status || 'pending',
  attendance: existing?.attendance || 'pending',
  scheduled_datetime: existing?.scheduled_datetime ? existing.scheduled_datetime.slice(0, 16) : '',
  ai_suggestion: existing?.ai_suggestion || '',
  is_extra: existing?.is_extra || false,
  extra_reason: existing?.extra_reason || ''
});

const buildRound = (index, checkpoints, existing) => ({
  ...(existing || {}),
  id: existing?.id || `demo_${index + 1}`,
  round_number: existing?.round_number || index + 1,
  checkpoints: existing?.checkpoints?.length
    ? existing.checkpoints.map(cp => ({
        name: cp.name,
        description: cp.description || '',
        met: cp.met === true ? true : cp.met === false ? false : null,
        notes: cp.notes || ''
      }))
    : checkpoints.map(cp => ({
        name: typeof cp === 'string' ? cp : cp.name,
        description: (typeof cp === 'string' ? '' : cp.description) || '',
        met: null,
        notes: ''
      })),
  feedback: existing?.feedback || '',
  status: existing?.status || 'pending',
  scheduled_datetime: existing?.scheduled_datetime ? existing.scheduled_datetime.slice(0, 16) : '',
  is_extra: existing?.is_extra || false,
  extra_reason: existing?.extra_reason || ''
});

/**
 * Single editor used by both the per-session pencil dialog and the global
 * "Candidate Performance" dialog. Renders the exact same session blocks as the
 * Add Performance dialog and always saves the configured session arrays.
 */
export const HRSessionEditDialog = ({
  isOpen,
  onClose,
  performance,
  candidateStatusId,
  candidateName,
  scope = { type: 'all' },
  getAuthToken,
  onSaved
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [briefingSessions, setBriefingSessions] = useState([]);
  const [demoRounds, setDemoRounds] = useState([]);
  const [performanceReport, setPerformanceReport] = useState('');
  const [extraDialog, setExtraDialog] = useState({ type: null, show: false });
  const [extraReason, setExtraReason] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${getAuthToken()}` };
        let perf = performance;
        if (candidateStatusId) {
          const res = await axios.get(`${API}/api/candidate-performance/${candidateStatusId}`, { headers });
          perf = res.data || performance;
        }
        const cfgRes = await axios.get(`${API}/api/hr-assessment-configs/for-selection`, {
          headers,
          params: {
            company_name: perf?.company_name || performance?.company_name || '',
            position_name: perf?.process || performance?.process || undefined
          }
        });
        const cfg = cfgRes.data || {};
        setConfig(cfg);
        const topics = cfg.briefing_topics || [];
        const checkpoints = cfg.demo_checkpoints || [];
        const existingBriefings = perf?.briefing_sessions || [];
        const existingRounds = perf?.demo_rounds || [];
        const briefingCount = Math.max(existingBriefings.length, cfg.default_briefing_sessions || 1);
        const roundCount = Math.max(existingRounds.length, cfg.default_demo_rounds || 1);
        setBriefingSessions(
          Array.from({ length: briefingCount }, (_, i) => buildSession(i, topics, existingBriefings[i]))
        );
        setDemoRounds(
          Array.from({ length: roundCount }, (_, i) => buildRound(i, checkpoints, existingRounds[i]))
        );
        setPerformanceReport(perf?.performance_report || '');
      } catch (error) {
        toast.error('Failed to load session data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, candidateStatusId]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSession = (sessionId, field, value) => {
    setBriefingSessions(prev => prev.map(s => (s.id === sessionId ? { ...s, [field]: value } : s)));
  };

  const updateTopic = (sessionId, topicIdx, covered) => {
    setBriefingSessions(prev => prev.map(s => {
      if (s.id !== sessionId) return s;
      const topics = [...s.topics];
      topics[topicIdx] = { ...topics[topicIdx], covered };
      return { ...s, topics };
    }));
  };

  const updateRound = (roundId, field, value) => {
    setDemoRounds(prev => prev.map(r => (r.id === roundId ? { ...r, [field]: value } : r)));
  };

  const updateCheckpoint = (roundId, cpIdx, field, value) => {
    setDemoRounds(prev => prev.map(r => {
      if (r.id !== roundId) return r;
      const checkpoints = [...r.checkpoints];
      checkpoints[cpIdx] = { ...checkpoints[cpIdx], [field]: value };
      return { ...r, checkpoints };
    }));
  };

  const addExtraSession = () => {
    if (extraDialog.type === 'briefing') {
      const index = briefingSessions.length;
      setBriefingSessions([
        ...briefingSessions,
        { ...buildSession(index, config?.briefing_topics || [], null), is_extra: true, extra_reason: extraReason }
      ]);
    } else {
      const index = demoRounds.length;
      setDemoRounds([
        ...demoRounds,
        { ...buildRound(index, config?.demo_checkpoints || [], null), is_extra: true, extra_reason: extraReason }
      ]);
    }
    setExtraDialog({ type: null, show: false });
    setExtraReason('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/candidate-performance`, {
        candidate_status_id: candidateStatusId,
        candidate_name: candidateName || performance?.candidate_name || '',
        company_name: performance?.company_name || '',
        process: performance?.process || '',
        briefing_sessions: briefingSessions,
        demo_rounds: demoRounds,
        performance_report: performanceReport,
        config_id: config?.id || performance?.config_id || null
      }, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      toast.success('Session data saved');
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save session data');
    } finally {
      setSaving(false);
    }
  };

  const showBriefings = scope.type === 'all' || scope.type === 'briefing';
  const showRounds = scope.type === 'all' || scope.type === 'demo';
  const visibleBriefings = scope.type === 'briefing'
    ? briefingSessions.filter((_, i) => i === scope.index)
    : briefingSessions;
  const visibleRounds = scope.type === 'demo'
    ? demoRounds.filter((_, i) => i === scope.index)
    : demoRounds;

  const title = scope.type === 'briefing'
    ? `Update Briefing Session ${scope.index + 1}`
    : scope.type === 'demo'
      ? `Update Demo HR Round ${scope.index + 1}`
      : `Candidate Performance - ${candidateName || ''}`;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="w-[calc(100vw-1rem)] md:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="hr-session-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">{title}</DialogTitle>
          <DialogDescription>
            Evaluation is Met / Not Met only — checkpoints and topics follow the configuration for this company and position.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
            <span className="ml-2 text-slate-500">Loading assessment structure...</span>
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {showBriefings && visibleBriefings.map((session) => (
              <BriefingSessionCard
                key={session.id}
                session={session}
                index={briefingSessions.indexOf(session)}
                onUpdate={(field, value) => updateSession(session.id, field, value)}
                onUpdateTopic={(topicIdx, covered) => updateTopic(session.id, topicIdx, covered)}
                getAuthToken={getAuthToken}
              />
            ))}

            {showBriefings && scope.type === 'all' && (
              <Button
                variant="outline"
                className="w-full border-dashed"
                onClick={() => setExtraDialog({ type: 'briefing', show: true })}
                data-testid="add-extra-briefing-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Extra Briefing Session
              </Button>
            )}

            {showRounds && visibleRounds.map((round) => (
              <DemoRoundCard
                key={round.id}
                round={round}
                index={demoRounds.indexOf(round)}
                onUpdate={(field, value) => updateRound(round.id, field, value)}
                onUpdateCheckpoint={(cpIdx, field, value) => updateCheckpoint(round.id, cpIdx, field, value)}
              />
            ))}

            {showRounds && scope.type === 'all' && (
              <Button
                variant="outline"
                className="w-full border-dashed"
                onClick={() => setExtraDialog({ type: 'demo', show: true })}
                data-testid="add-extra-demo-round-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Extra Demo HR Round
              </Button>
            )}

            {scope.type === 'all' && (
              <div className="border rounded-xl p-4 space-y-2 bg-slate-50">
                <Label className="text-xs font-semibold">Performance Report</Label>
                <Textarea
                  value={performanceReport}
                  onChange={(e) => setPerformanceReport(e.target.value)}
                  rows={4}
                  placeholder="Overall performance assessment..."
                  data-testid="performance-report-input"
                />
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} data-testid="cancel-session-edit-btn">Cancel</Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-gradient-to-r from-blue-500 to-indigo-600"
                data-testid="save-session-edit-btn"
              >
                {saving ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" />Save Changes</>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        <Dialog open={extraDialog.show} onOpenChange={(open) => { if (!open) { setExtraDialog({ type: null, show: false }); setExtraReason(''); } }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                Add Extra {extraDialog.type === 'briefing' ? 'Briefing Session' : 'Demo HR Round'}
              </DialogTitle>
              <DialogDescription>Please provide a reason for adding an additional session</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>Reason / Justification *</Label>
              <Textarea
                value={extraReason}
                onChange={(e) => setExtraReason(e.target.value)}
                placeholder="Why is an extra session needed?"
                className="mt-2"
                rows={3}
                data-testid="extra-session-reason-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setExtraDialog({ type: null, show: false }); setExtraReason(''); }}>
                Cancel
              </Button>
              <Button onClick={addExtraSession} disabled={!extraReason.trim()} data-testid="confirm-extra-session-btn">
                Add Session
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default HRSessionEditDialog;
